import { clampInside, firstInside, hasGridInside, isOnGrid, lastInside, snapToGrid } from './gridInstant'
import { formatIsoInstant, parseIsoInstant } from './timeGrid'

// CorrectionDraft (03-dominio.md, RN45-RN55): what the user is describing in the panel, as a plain object with pure
// functions. Instants are epoch milliseconds; `NaN` is a field the user left empty or unreadable and `0` is «none» (a VOID has no
// time, «Back at…» may be empty). Tapes are always kept in the stable order JOB_STATE, PRESENCE.
//
//   { kind, newInstant, resumeAt, tapes, tapesTouched, reasonCode, comment }

const KIND_ORDER = ['ADJUST_END', 'ADJUST_START', 'VOID']
const TAPE_ORDER = ['JOB_STATE', 'PRESENCE']
const PRESENCE_TABLE = 'DEVICE_WORKER'
export const MAX_COMMENT_LENGTH = 1000

const isOpen = (intervention) => intervention.open === true

/**
 * Story 211, RN20: an open intervention has no recorded end, so the `now` of the reading stands in for it everywhere F2 uses the
 * recorded end. A closed one comes back as it is.
 */
export const withRecordedEnd = (intervention, nowMs) => (isOpen(intervention) ? { ...intervention, end: formatIsoInstant(nowMs) } : intervention)

const startOf = (intervention) => parseIsoInstant(intervention.start).getTime()
const endOf = (intervention) => parseIsoInstant(intervention.end).getTime()

/** RN45 [GATE2-G6]: ADJUST_END when it is allowed; otherwise the first allowed one in the order ADJUST_END, ADJUST_START, VOID. */
export const defaultKind = (allowedKinds) => KIND_ORDER.find((kind) => allowedKinds.includes(kind)) || 'VOID'

/** RN47: an ADJUST_* needs some instant of the grid strictly inside the record; if there is none, only VOID is left. */
export const canAdjust = (intervention) => hasGridInside(startOf(intervention), endOf(intervention))

/**
 * RN45 [A202-14]: the allowed kinds that can really be used. An intervention too short to adjust in 5-minute steps keeps only
 * VOID (S50). An open one is measured up to the `now` of the reading (RN20, see `withRecordedEnd`).
 */
export const usableKinds = (intervention) => (canAdjust(intervention)
  ? intervention.allowedKinds
  : intervention.allowedKinds.filter((kind) => kind === 'VOID'))

/** The recorded edge a kind starts from (RN45): the end for ADJUST_END, the start for ADJUST_START, none for VOID. */
export const edgeOf = (kind, intervention) => {
  if (kind === 'ADJUST_END') return endOf(intervention)
  if (kind === 'ADJUST_START') return startOf(intervention)
  return 0
}

/** Whether the tape JOB_STATE can be asked for: only for a job anchor that has a job (RN49). */
export const canCorrectJobState = (intervention) =>
  intervention.anchor.table !== PRESENCE_TABLE && intervention.job !== null && intervention.job !== undefined

/** RN49 [D5]: a presence anchor starts with PRESENCE; a job anchor adds JOB_STATE only when nobody else worked on the job. */
export const defaultTapes = (intervention) =>
  canCorrectJobState(intervention) && intervention.otherWorkersOnJob.length === 0 ? ['JOB_STATE', 'PRESENCE'] : ['PRESENCE']

export const createDraft = (intervention) => {
  const kind = defaultKind(usableKinds(intervention))
  return { kind, newInstant: edgeOf(kind, intervention), resumeAt: 0, tapes: defaultTapes(intervention), tapesTouched: false, reasonCode: '', comment: '' }
}

export const withKind = (draft, kind, intervention) => ({ ...draft, kind, newInstant: edgeOf(kind, intervention) })

/**
 * A time typed in the field (RN47, G4): the recorded edge stays as it is (it means «no change», and it is off the grid);
 * anything else snaps to the grid and is NOT clamped, so a value outside the record shows its error.
 */
export const withTypedInstant = (draft, ms, intervention) => {
  if (Number.isNaN(ms)) return { ...draft, newInstant: Number.NaN }
  return { ...draft, newInstant: ms === edgeOf(draft.kind, intervention) ? ms : snapToGrid(ms) }
}

/** A time reached by dragging a handle or with the keyboard: it sets the kind and is clamped inside the record (RN40, RN47). */
export const withDraggedInstant = (draft, kind, ms, intervention) => ({
  ...draft,
  kind,
  newInstant: clampInside(snapToGrid(ms), startOf(intervention), endOf(intervention))
})

export const withResumeAt = (draft, ms) => ({ ...draft, resumeAt: Number.isNaN(ms) ? 0 : ms })

export const withTapes = (draft, tapes, touched) => ({
  ...draft,
  tapes: TAPE_ORDER.filter((tape) => tapes.includes(tape)),
  tapesTouched: touched
})

export const withReason = (draft, reasonCode) => ({ ...draft, reasonCode })
export const withComment = (draft, comment) => ({ ...draft, comment })

const sameSet = (a, b) => a.length === b.length && a.every((tape) => b.includes(tape))

/** RN49: after the FIRST correct preview, untouched tapes become the ones Sun suggests (only once). */
export const adoptSuggested = (draft, suggested, { first }) => {
  if (!first || draft.tapesTouched || sameSet(draft.tapes, suggested)) return { draft, adopted: false }
  return { draft: withTapes(draft, suggested, false), adopted: true }
}

/** RN47: «no change» = ADJUST_* with the time on the recorded edge. VOID always is a change. */
export const isNoChange = (draft, intervention) => draft.kind !== 'VOID' && draft.newInstant === edgeOf(draft.kind, intervention)

/** RN50: the local stretch W, [start, end] in ms; `NaN`s when the time is not readable. */
export const windowOf = (draft, intervention) => {
  const start = startOf(intervention)
  const end = endOf(intervention)
  if (draft.kind === 'VOID') return { startMs: start, endMs: end }
  if (!Number.isFinite(draft.newInstant)) return { startMs: Number.NaN, endMs: Number.NaN }
  return draft.kind === 'ADJUST_END' ? { startMs: draft.newInstant, endMs: end } : { startMs: start, endMs: draft.newInstant }
}

/** RN51 [D7]: «Back at…» exists only with PRESENCE and when a presence row of the worker crosses W. */
export const showsResumeAt = (draft, intervention, presenceRows) => {
  if (isOpen(intervention)) return false // RN21: after the close the presence ends at the close and does not cross the stretch
  if (!draft.tapes.includes('PRESENCE')) return false
  const { startMs, endMs } = windowOf(draft, intervention)
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return false
  return presenceRows.some((row) => row.worker.id === intervention.worker.id &&
    parseIsoInstant(row.start).getTime() <= startMs && (row.open === true || parseIsoInstant(row.end).getTime() > endMs))
}

/** The «Back at…» that really goes: empty when the field is hidden (it would be RESUME_AT_WITHOUT_PRESENCE). */
export const effectiveResumeAt = (draft, intervention, presenceRows) => (showsResumeAt(draft, intervention, presenceRows) ? draft.resumeAt : 0)

/** RN48: the local errors of the time field alone (the field is in error only with one of them, A202-17). */
export const validateTime = (draft, intervention, format) => {
  const errors = []
  const start = startOf(intervention)
  const end = endOf(intervention)
  if (draft.kind === 'ADJUST_END' || draft.kind === 'ADJUST_START') {
    const instant = draft.newInstant
    if (!Number.isFinite(instant)) errors.push({ key: 'Enter a valid date and time.', params: {} })
    else if (draft.kind === 'ADJUST_END') {
      if (instant > end) {
        errors.push(isOpen(intervention)
          ? { key: 'The real end time cannot be later than now.', params: {} }
          : { key: 'The real end time cannot be later than the recorded end. In this version a record can only be shortened or voided.', params: {} })
      } else if (instant <= start) errors.push({ key: 'The real end time must be later than the start ({{date}}). If the intervention should not exist, use «Void».', params: { date: format(start) } })
    } else if (instant < start) errors.push({ key: 'The real start time cannot be earlier than the recorded start. In this version a record can only be shortened or voided.', params: {} })
    else if (instant >= end) errors.push({ key: 'The real start time must be earlier than the end ({{date}}). If the intervention should not exist, use «Void».', params: { date: format(end) } })
  }
  return errors
}

/** RN48: the local errors, as UiMessages `{key, params}`. `format(ms)` writes an instant in local time. */
export const validate = (draft, intervention, presenceRows, format) => {
  const errors = validateTime(draft, intervention, format)
  if (draft.tapes.length === 0) errors.push({ key: 'Choose at least one tape to correct.', params: {} })
  if (showsResumeAt(draft, intervention, presenceRows) && draft.resumeAt !== 0) {
    const { startMs, endMs } = windowOf(draft, intervention)
    if (!(draft.resumeAt > startMs && draft.resumeAt < endMs) || !isOnGrid(draft.resumeAt)) {
      errors.push({ key: '«Back at…» must be between {{start}} and {{end}}.', params: { start: format(startMs), end: format(endMs) } })
    }
  }
  return errors
}

/** RN56: a preview is asked only for a draft that is a change and has no local error (an open one too, RN23). */
export const canPreview = (draft, intervention, presenceRows, format) =>
  !isNoChange(draft, intervention) && validate(draft, intervention, presenceRows, format).length === 0

/** RN55 (#201 §2.1): the exact intent. Ids are numbers, instants ISO UTC; reason and comment are not part of it. */
export const toIntent = (draft, intervention, deviceId, presenceRows) => {
  const resumeAt = effectiveResumeAt(draft, intervention, presenceRows)
  return {
    kind: draft.kind,
    deviceId,
    workerId: intervention.worker.id,
    fabricationOrderId: intervention.fabricationOrder ? intervention.fabricationOrder.id : null,
    jobReferenceId: intervention.job ? intervention.job.id : null,
    anchor: { table: intervention.anchor.table, rowId: intervention.anchor.rowId },
    newInstant: draft.kind === 'VOID' ? null : formatIsoInstant(draft.newInstant),
    resumeAt: resumeAt === 0 ? null : formatIsoInstant(resumeAt),
    tapes: TAPE_ORDER.filter((tape) => draft.tapes.includes(tape)),
    interruptStatusId: null
  }
}

/** RN60: the canonical text of an intent, field by field, to tell whether two intents are the same one. */
export const intentKey = (intent) => JSON.stringify([
  intent.kind, intent.deviceId, intent.workerId, intent.fabricationOrderId, intent.jobReferenceId,
  intent.anchor.table, intent.anchor.rowId, intent.newInstant, intent.resumeAt, intent.tapes, intent.interruptStatusId
])

/** RN53 [G14]: required with a reason that asks for it; the counter counts the text as typed; what goes is trimmed. */
export const commentRules = (comment, requiresComment) => {
  const sent = comment.trim()
  return {
    count: comment.length,
    max: MAX_COMMENT_LENGTH,
    required: requiresComment,
    valid: comment.length <= MAX_COMMENT_LENGTH && !(requiresComment && sent === ''),
    sent: comment.length <= MAX_COMMENT_LENGTH ? sent : ''
  }
}

export { firstInside, lastInside }
